<template>
    <div class="insights-table">
        <NewSupsisTable
            :columns="columns"
            :data="enrichedRows"
            row-key="id"
            :show-column-add="true"
            :resizable="true"
            :sortable-columns="true"
            :local-storage-key="tableId"
            @row-click="onRowClick"
        >
            <template v-slot:cell-thumbnail="{ row }">
                <img
                    v-if="row.creative && row.creative.thumbnail_url"
                    :src="row.creative.thumbnail_url"
                    alt="creative"
                    class="mi-thumb"
                />
                <div v-else class="mi-thumb placeholder"></div>
            </template>
            <template v-slot:cell-name="{ row }">
                <span class="row-name">{{ row.name || row.id }}</span>
            </template>
            <template v-slot:cell-status="{ row }">
                <span
                    v-if="getStatusValue(row)"
                    class="mi-status-badge"
                    :class="String(getStatusValue(row)).toLowerCase()"
                >
                    {{ getStatusLabel(row) }}
                </span>
                <span v-else>-</span>
            </template>
            <template v-slot:cell-spend="{ row }">{{ formatCurrency(row._ins.spend) }}</template>
            <template v-slot:cell-impressions="{ row }">{{ formatNumber(row._ins.impressions) }}</template>
            <template v-slot:cell-reach="{ row }">{{ formatNumber(row._ins.reach) }}</template>
            <template v-slot:cell-clicks="{ row }">{{ formatNumber(row._ins.clicks) }}</template>
            <template v-slot:cell-ctr="{ row }">{{ formatPercent(row._ins.ctr) }}</template>
            <template v-slot:cell-cpm="{ row }">{{ formatCurrency(row._ins.cpm) }}</template>
            <template v-slot:cell-cpc="{ row }">{{ formatCurrency(row._ins.cpc) }}</template>
            <template v-slot:cell-frequency="{ row }">{{ formatNumber(row._ins.frequency) }}</template>
            <template v-slot:cell-linkClicks="{ row }">{{ formatNumber(row._ins.linkClicks) }}</template>
            <template v-slot:cell-costPerLinkClick="{ row }">{{ formatCurrency(row._ins.costPerLinkClick) }}</template>
            <template v-slot:cell-leads="{ row }">{{ formatNumber(row._ins.leads) }}</template>
            <template v-slot:cell-costPerLead="{ row }">{{ formatCurrency(row._ins.costPerLead) }}</template>
            <template v-slot:cell-pageEngagement="{ row }">{{ formatNumber(row._ins.pageEngagement) }}</template>
            <template v-slot:cell-postEngagement="{ row }">{{ formatNumber(row._ins.postEngagement) }}</template>
            <template v-slot:cell-videoViews="{ row }">{{ formatNumber(row._ins.videoViews) }}</template>
            <template v-slot:cell-reactions="{ row }">{{ formatNumber(row._ins.reactions) }}</template>
            <template v-slot:cell-comments="{ row }">{{ formatNumber(row._ins.comments) }}</template>
            <template v-slot:cell-shares="{ row }">{{ formatNumber(row._ins.shares) }}</template>
            <template v-slot:cell-messagingStarted="{ row }">{{ formatNumber(row._ins.messagingStarted) }}</template>
        </NewSupsisTable>
    </div>
</template>

<script>
import _ from "lodash";
import NewSupsisTable from "../../../../../components/common/NewSupsisTable.vue";

const formatNumber = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    const num = Number(val);
    if (isNaN(num)) return val;
    return num.toLocaleString("tr-TR", { maximumFractionDigits: 2 });
};

// actions array'inden bir action_type'in value'sunu doner (number)
const getActionValue = (actions, type) => {
    if (!Array.isArray(actions)) return null;
    const found = actions.find((a) => a && a.action_type === type);
    if (!found) return null;
    const num = Number(found.value);
    return isNaN(num) ? null : num;
};

export default {
    name: "InsightsTable",
    components: { NewSupsisTable },
    props: {
        rows: {
            type: Array,
            default: () => [],
        },
        currency: {
            type: String,
            default: "",
        },
        tableId: {
            type: String,
            default: "meta-insights-table",
        },
        nameLabelKey: {
            type: String,
            default: "metaInsights.col.name",
        },
        showCreative: {
            type: Boolean,
            default: false,
        },
        clickable: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        enrichedRows() {
            return (this.rows || []).map((r) => {
                const raw = _.get(r, "insights.data[0]") || {};
                const actions = raw.actions || [];
                const costs = raw.cost_per_action_type || [];

                const linkClicks = getActionValue(actions, "link_click");
                const leads = getActionValue(actions, "lead");
                const pageEngagement = getActionValue(actions, "page_engagement");
                const postEngagement = getActionValue(actions, "post_engagement");
                const videoViews = getActionValue(actions, "video_view");
                const reactions = getActionValue(actions, "post_reaction");
                const comments = getActionValue(actions, "comment");
                const shares = getActionValue(actions, "post");
                const messagingStarted = getActionValue(
                    actions,
                    "onsite_conversion.messaging_conversation_started_7d"
                );

                const ins = {
                    spend: raw.spend,
                    impressions: raw.impressions,
                    reach: raw.reach,
                    clicks: raw.clicks,
                    ctr: raw.ctr,
                    cpm: raw.cpm,
                    cpc: raw.cpc,
                    frequency: raw.frequency,
                    linkClicks: linkClicks,
                    costPerLinkClick: getActionValue(costs, "link_click"),
                    leads: leads,
                    costPerLead: getActionValue(costs, "lead"),
                    pageEngagement: pageEngagement,
                    postEngagement: postEngagement,
                    videoViews: videoViews,
                    reactions: reactions,
                    comments: comments,
                    shares: shares,
                    messagingStarted: messagingStarted,
                };
                return Object.assign({}, r, { _ins: ins });
            });
        },
        columns() {
            const t = this.$t.bind(this);
            const cols = [];
            if (this.showCreative) {
                cols.push({ key: "thumbnail", title: "", width: 60, visible: true, selectable: false });
            }
            cols.push({
                key: "name",
                title: t(this.nameLabelKey),
                width: 280,
                visible: true,
                selectable: false,
            });
            cols.push({
                key: "status",
                title: t("metaInsights.col.status"),
                width: 130,
                visible: true,
                sortable: true,
            });

            // ==== Default-VISIBLE: kullanici tabloyu acar acmaz gorur ====
            // Para / hacim
            cols.push(this.col("spend", "metaInsights.col.spend", 130, true));
            cols.push(this.col("reach", "metaInsights.col.reach", 110, true));
            cols.push(this.col("impressions", "metaInsights.col.impressions", 130, true));
            cols.push(this.col("frequency", "metaInsights.col.frequency", 130, true));
            // Tiklamalar
            cols.push(this.col("clicks", "metaInsights.col.clicks", 110, true));
            cols.push(this.col("linkClicks", "metaInsights.col.linkClicks", 140, true));
            // Oranlar / birim maliyet
            cols.push(this.col("ctr", "metaInsights.col.ctr", 130, true));
            cols.push(this.col("cpc", "metaInsights.col.cpc", 130, true));
            cols.push(this.col("cpm", "metaInsights.col.cpm", 130, true));
            cols.push(this.col("costPerLinkClick", "metaInsights.col.costPerLinkClick", 160, true));

            // ==== Default-HIDDEN: kullanici "Kolonlari Yonet"'ten acar ====
            // Lead / dönüsüm
            cols.push(this.col("leads", "metaInsights.col.leads", 130, false));
            cols.push(this.col("costPerLead", "metaInsights.col.costPerLead", 160, false));
            // Etkilesim
            cols.push(this.col("pageEngagement", "metaInsights.col.pageEngagement", 150, false));
            cols.push(this.col("postEngagement", "metaInsights.col.postEngagement", 150, false));
            cols.push(this.col("reactions", "metaInsights.col.reactions", 130, false));
            cols.push(this.col("comments", "metaInsights.col.comments", 110, false));
            cols.push(this.col("shares", "metaInsights.col.shares", 110, false));
            // Video / mesajlasma
            cols.push(this.col("videoViews", "metaInsights.col.videoViews", 130, false));
            cols.push(this.col("messagingStarted", "metaInsights.col.messagingStarted", 170, false));
            return cols;
        },
    },
    methods: {
        col(key, titleKey, width, visible) {
            return {
                key: key,
                title: this.$t(titleKey),
                width: width,
                visible: visible,
                sortable: true,
            };
        },
        formatNumber: formatNumber,
        formatPercent(val) {
            if (val === null || val === undefined || val === "") return "-";
            const num = Number(val);
            if (isNaN(num)) return val;
            return num.toLocaleString("tr-TR", { maximumFractionDigits: 2 }) + "%";
        },
        formatCurrency(val) {
            if (val === null || val === undefined || val === "") return "-";
            const num = Number(val);
            if (isNaN(num)) return val;
            return num.toLocaleString("tr-TR", { maximumFractionDigits: 2 }) + " " + (this.currency || "");
        },
        onRowClick(row) {
            if (!this.clickable) return;
            this.$emit("row-click", row);
        },
        getStatusValue(row) {
            return row.effective_status || row.status || "";
        },
        getStatusLabel(row) {
            const value = this.getStatusValue(row);
            if (!value) return "";
            const key = "metaInsights.status." + value;
            const translated = this.$t(key);
            // i18n key bulunmadiysa raw degeri okunabilir hale getir
            if (translated === key) {
                return value.replace(/_/g, " ");
            }
            return translated;
        },
    },
};
</script>

<style lang="scss">
.insights-table {
    width: 100%;

    .mi-thumb {
        width: 36px;
        height: 36px;
        object-fit: cover;
        border-radius: 4px;

        &.placeholder {
            background: #f0f0f0;
        }
    }

    .row-name {
        font-weight: 500;
    }

    .mi-status-badge {
        display: inline-block;
        padding: 2px 10px;
        border-radius: 10px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        background: #f5f5f5;
        color: #595959;
        white-space: nowrap;

        &.active {
            background: #f6ffed;
            color: #389e0d;
            border: 1px solid #b7eb8f;
        }

        &.paused,
        &.campaign_paused,
        &.adset_paused {
            background: #fff7e6;
            color: #d46b08;
            border: 1px solid #ffd591;
        }

        &.deleted,
        &.archived,
        &.campaign_deleted,
        &.adset_deleted {
            background: #fafafa;
            color: #8c8c8c;
            border: 1px solid #d9d9d9;
        }

        &.disapproved,
        &.with_issues {
            background: #fff1f0;
            color: #cf1322;
            border: 1px solid #ffa39e;
        }

        &.in_process,
        &.pending_review,
        &.pending_billing_info {
            background: #e6f7ff;
            color: #1890ff;
            border: 1px solid #91d5ff;
        }
    }
}
</style>
